import React, { useState, useRef } from 'react';
import {
  Mail,
  Phone,
  Send,
  MapPin,
  Loader2,
  CheckCircle,
  Paperclip,
  Image,
  X,
  Building2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

interface Attachment {
  id: string;
  file: File;
  type: 'file' | 'image';
  previewUrl?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

export function Contact() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      setSent(true);
      attachments.forEach(attachment => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'file' | 'image'
  ) => {
    const files = e.target.files;
    if (!files?.length) return;

    const newAttachments: Attachment[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }

      if (type === 'image' && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Invalid image type. Allowed types: JPEG, PNG, GIF`);
        continue;
      }

      if (type === 'file' && !ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(
          `${file.name}: Invalid file type. Allowed types: PDF, DOC, DOCX, XLS, XLSX, TXT`
        );
        continue;
      }

      const attachment: Attachment = {
        id: crypto.randomUUID(),
        file,
        type,
        previewUrl: type === 'image' ? URL.createObjectURL(file) : undefined,
      };
      newAttachments.push(attachment);
    }

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
    }

    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments]);
      toast.success(`${newAttachments.length} file(s) attached successfully`);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  if (sent) {
    return (
      <div className="mx-auto max-w-3xl py-12">
        <div className="rounded-2xl bg-gradient-to-br from-purple-50 to-white p-8 text-center shadow-lg">
          <CheckCircle className="mx-auto h-12 w-12 text-purple-500" />
          <h2 className="mt-4 text-2xl font-semibold text-gray-900">Message Sent Successfully!</h2>
          <p className="mt-2 text-gray-600">
            Thank you for contacting us. We'll get back to you as soon as possible.
          </p>
          <button
            onClick={() => {
              setSent(false);
              setForm({
                name: '',
                email: '',
                phone: '',
                company: '',
                message: '',
              });
              setAttachments([]);
            }}
            className="mt-6 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-7xl py-12">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-1/4 h-[500px] w-[500px] rounded-full bg-purple-100 opacity-50 blur-3xl"></div>
        <div className="absolute -right-1/4 bottom-1/4 h-[500px] w-[500px] rounded-full bg-purple-100 opacity-50 blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <div className="rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-900/5">
            <header className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900">Contact Us</h1>
              <p className="mt-2 text-gray-600">Get in touch with our team for any questions or support</p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1 flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="block w-full rounded-r-lg border border-gray-300 px-4 py-2.5 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="block w-full rounded-r-lg border border-gray-300 px-4 py-2.5 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="mt-1 flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      className="block w-full rounded-r-lg border border-gray-300 px-4 py-2.5 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <div className="mt-1 flex rounded-lg shadow-sm">
                    <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                    </span>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="block w-full rounded-r-lg border border-gray-300 px-4 py-2.5 focus:border-purple-500 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  value={form.message}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              {/* File Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Attachments</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileSelect(e, 'file')}
                    className="hidden"
                    multiple
                    accept={ALLOWED_FILE_TYPES.join(',')}
                  />
                  <input
                    type="file"
                    ref={imageInputRef}
                    onChange={(e) => handleFileSelect(e, 'image')}
                    className="hidden"
                    multiple
                    accept={ALLOWED_IMAGE_TYPES.join(',')}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    <Paperclip className="mr-2 h-4 w-4" />
                    Attach Files
                  </button>
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    <Image className="mr-2 h-4 w-4" />
                    Add Images
                  </button>
                </div>

                {/* Attachment Previews */}
                {attachments.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="relative rounded-lg border border-gray-200 bg-gray-50 p-2"
                      >
                        {attachment.type === 'image' && attachment.previewUrl ? (
                          <img
                            src={attachment.previewUrl}
                            alt={attachment.file.name}
                            className="h-20 w-20 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-gray-100">
                            <Paperclip className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow-sm hover:bg-gray-100"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                        <p className="mt-1 max-w-[7rem] truncate text-xs text-gray-500">
                          {attachment.file.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-md disabled:opacity-50"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8 lg:pl-8">
            <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 p-8 text-white shadow-lg">
              <h2 className="text-xl font-semibold">Get in Touch</h2>
              <p className="mt-2 text-purple-100">
                We're here to help! Reach out through any of these channels.
              </p>

              <div className="mt-8 space-y-6">
                <a
                  href="mailto:support@bidzora.com"
                  className="flex items-center space-x-4 rounded-lg bg-white/10 p-4 transition-colors hover:bg-white/20"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-medium">Email Us</p>
                    <p className="text-sm text-purple-200">support@bidzora.com</p>
                  </div>
                </a>

                <a
                  href="tel:+15551234567"
                  className="flex items-center space-x-4 rounded-lg bg-white/10 p-4 transition-colors hover:bg-white/20"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-medium">Call Us</p>
                    <p className="text-sm text-purple-200">+1 (555) 123-4567</p>
                  </div>
                </a>

                <div className="flex items-center space-x-4 rounded-lg bg-white/10 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/20">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-medium">Visit Us</p>
                    <p className="text-sm text-purple-200">
                      123 Business Ave, Suite 100
                      <br />
                      Washington, DC 20001
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map or Additional Content */}
            <div className="overflow-hidden rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-900/5">
              <h2 className="text-xl font-semibold text-gray-900">Business Hours</h2>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monday - Friday</span>
                  <span className="font-medium text-gray-900">9:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Saturday</span>
                  <span className="font-medium text-gray-900">10:00 AM - 4:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sunday</span>
                  <span className="font-medium text-gray-900">Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}